import React, { ChangeEvent, useState } from 'react';
import styles from './NewGroup.module.scss';
import AdminProjectList from '../../../../../../jobMatch/JobMatchList/AdminProjectList';
import { NumberSchema, ObjectSchema, StringSchema } from 'yup';
import useDebounce from '../../../../../../../../helpers/useDebounce';
import ColorInput from '../../../../../../input/ColorInput/ColorInput';
import Color from '../../../../../../../../helpers/Color';
import { useTranslate } from 'react-translate.ts';
import AdminInput from '../../../../../../input/AdminInput';

interface NewGroupProps {
    onCreate: (
        name: string,
        color: string,
        minimalEvaluatorCount: number,
    ) => void;
}

interface Values {
    name: string;
    color: string;
    minimalEvaluatorCount: number;
}

const defaultValues = {
    name: '',
    color: '',
    minimalEvaluatorCount: 0,
};

const validationSchema = new ObjectSchema({
    name: new StringSchema().nonNullable().required(),
    color: new StringSchema().nonNullable().required(),
    minimalEvaluatorCount: new NumberSchema(),
});

export default function NewGroup(props: NewGroupProps) {
    const [values, setValues] = useState<Values>(defaultValues);
    const debounce = useDebounce();
    const translate = useTranslate();

    return (
        <AdminProjectList.Row className={styles.row}>
            <AdminInput
                value={values.name}
                onChange={handleNameChange(values)}
                placeholder={translate('Name')}
            />
            <ColorInput
                color={values.color}
                onChange={handleColorChange(values)}
                placeholder={translate('Color')}
            />
            <AdminInput
                type="number"
                value={values.minimalEvaluatorCount}
                onChange={handleCountChange(values)}
                placeholder={translate('Minimal Evaluator Count')}
            />
        </AdminProjectList.Row>
    );

    function handleNameChange(values: Values) {
        return async (event: ChangeEvent<HTMLInputElement>) => {
            const name: string = event.target.value;
            const newValues = { ...values, name };
            setValues(newValues);

            if (await validate(newValues)) {
                await debounce(create, newValues);
            }
        };
    }

    function handleCountChange(values: Values) {
        return async (event: ChangeEvent<HTMLInputElement>) => {
            const minimalEvaluatorCount: number = parseInt(event.target.value);
            if (isNaN(minimalEvaluatorCount)) {
                return;
            }

            const newValues = { ...values, minimalEvaluatorCount };
            setValues(newValues);

            if (await validate(newValues)) {
                await debounce(create, newValues);
            }
        };
    }

    function handleColorChange(values: Values) {
        return async (color: string) => {
            const newValues = { ...values, color };
            setValues(newValues);

            if (await validate(newValues)) {
                await debounce(create, newValues);
            }
        };
    }

    function create(values: Values) {
        const { name, color, minimalEvaluatorCount } = values;
        props.onCreate(name, color, minimalEvaluatorCount);
    }

    async function validate(values: Values) {
        return (
            Color.validate(values.color) &&
            (await validationSchema.validate(values))
        );
    }
}
